import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useGetPortalCustomAttributesDefinitions } from 'api/services/BrandPortal/Ingredients';
import { useLogEvent } from 'api/services/LogEventService';
import { useLogSearchRecord } from 'api/services/SearchService';
import { getProductTypeSingulars } from 'api/types/ReferenceData';
import {
  createPageTitle,
  IS_UXBEAUTY,
  WHITELABEL_EVENT_KEY
} from 'components/constants/Constants';
import { onBookmarkChange, SearchProvider } from 'context/SearchContext';
import {
  clearAllUrlFilterParams,
  combineCustomAttributes,
  filterActiveFiltersByKey,
  getFiltersWithDifferentType,
  getInitialSortState,
  mapSearchStateToRequest,
  parseUrlFilterParams,
  updateUrlFilterParams
} from 'helpers/Search';

import useSupplierPortalUser from 'hooks/useSupplierPortalUser';
import piwik, { trackMatomoEvent } from 'piwik/piwik';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useParams, useSearchParams } from 'react-router-dom';
import { SearchCardView } from 'supplier-portals/uxbeauty/pages/Search/CardView/SearchCardView';
import SearchListView from 'supplier-portals/uxbeauty/pages/Search/SearchListView';
import { ThemeProvider, getIncosmeticsTheme } from 'theme/Theme';
import { tss } from 'tss-react/mui';
import { LogEventType } from 'types/LogEvents';
import { ProductType } from 'types/Products';
import {
  ActiveFilter,
  FacetOrder,
  SearchDocument,
  SearchFilter,
  SearchRequest,
  SearchResults,
  SearchSortField,
  SearchSource,
  SortDirection
} from 'types/Search';
import { FetchResponse, Option, SegmentTag } from 'types/global';
import { SpinnerMui } from '../Spinner/SpinnerMui';
import InCosmeticsHeader from './InCosmeticsHeader/InCosmeticsHeader';
import { CompareRequestBulkRequestBar } from './SearchCompareBar/CompareRequestBulkRequestBar';
import { FacetOrderSelector } from './SearchFilters/FacetOrderSelector';
import { Filters } from './SearchFilters/Filters';
import { SearchHeader } from './SearchHeader/SearchHeader';
import { SearchResultMode } from './SearchHeader/SearchResultModeToggle';
import { BrandPortalTable } from './SearchTable/BrandPortalTable';
import SearchHighlightedCategories from './SearchTopCategories/SearchHighlightedCategories';
import { WhyRegisterModal } from './SignInDialogs/WhyRegisterModal';
import { useBookmark } from './useBookmark';
import { useSearch } from './useSearch';
import {
  COMPARE_BULK_LIMIT,
  REQUEST_BULK_LIMIT,
  useSelection
} from './useSelection';
import { CustomAttributeEntity } from '../Portals/CustomAttributes/types';
import { AxiosResponse } from 'axios';
import { useSearchStateReducer } from './SearchStateReducer';
import { formatSearchLog } from 'helpers/utilsTS';
import { CovaloTable } from './SearchTable/CovaloTable';
import SearchFilterClearButton from './SearchFilters/SearchFilterClearButton';
import { envs } from '../../../../envs';
import { capitalizeFirstLetter } from 'components/pages/products/ProductTable/columns/helper';

const PAGE_SIZE = 40;

const SEARCH_RESULT_MODE_LOCAL_STORAGE_KEY = 'searchResultMode';

interface SearchProps {
  target: ProductType;
}

const Search: React.FC<SearchProps> = ({ target }) => {
  const [facetOrder, setFacetOrder] = useState<FacetOrder>({
    value: 'COUNT',
    label: 'Count'
  });

  const { t } = useTranslation();

  // STATE DEFINITION ----------------------------
  const [searchParams, setSearchParams] = useSearchParams();

  const { event: eventKeyFromUrl } = useParams();

  const eventKey = WHITELABEL_EVENT_KEY ?? eventKeyFromUrl;

  const isCustomerPortal = WHITELABEL_EVENT_KEY && !IS_UXBEAUTY;

  const { hasSearchViewPermission: isBrandPortalMember } =
    useSupplierPortalUser(eventKey);

  const isBrandPortal: boolean =
    isBrandPortalMember && eventKey !== undefined && !IS_UXBEAUTY;

  const [searchState, dispatchNewSearchState] = useSearchStateReducer(
    PAGE_SIZE,
    {
      query: searchParams.get('query') ?? '',
      target: target,
      segment:
        (searchParams.get('segment')?.toUpperCase() as SegmentTag) ??
        SegmentTag.PERSONALCARE,
      size: PAGE_SIZE,
      from: 0,
      //its explicitely set as undefined, as indicator that the first request was not yet made
      filters: undefined,
      sort: getInitialSortState(target),
      eventKey: eventKey,
      source: SearchSource.ALL,
      hideLoadingIndicator: false,
      isFetchingMore: false
    },
    IS_UXBEAUTY
  );

  const isInCosmeticsBranding =
    searchState.segment === SegmentTag.PERSONALCARE &&
    !isBrandPortal &&
    !IS_UXBEAUTY &&
    !isCustomerPortal;

  const [userInteractionsCount, setUserInteractionsCount] = useState(0);
  const [searchResultMode, setSearchResultMode] = useState<SearchResultMode>(
    (localStorage.getItem(
      SEARCH_RESULT_MODE_LOCAL_STORAGE_KEY
    ) as SearchResultMode) ||
      (IS_UXBEAUTY ? SearchResultMode.GRID : SearchResultMode.LIST)
  );
  const { classes } = useStyles();
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const { isAuthenticated } = useAuth();

  const [
    { data: companyAttributesDefinitions, loading: companyAttributesLoading }
  ] = useGetPortalCustomAttributesDefinitions(
    eventKey ?? '',
    CustomAttributeEntity.COMPANY,
    !eventKey || IS_UXBEAUTY
  );

  const [
    {
      data: productAttributesDefinitions,
      loading: productAttributesLoading,
      error: productAttributesError
    }
  ] = useGetPortalCustomAttributesDefinitions(
    eventKey ?? '',
    CustomAttributeEntity.PRODUCT,
    !eventKey || IS_UXBEAUTY
  );

  const logEvent = useLogEvent();
  // remove invalid search params
  useEffect(() => {
    if (searchParams.get('query') === '') {
      searchParams.delete('query');
    }
    if (
      !Object.values(SegmentTag).includes(
        searchParams.get('segment')?.toUpperCase() as SegmentTag
      )
    ) {
      searchParams.delete('segment');
    }
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const [searchResults, setSearchResults] = useState<SearchResults>({
    total: 0,
    results: [],
    facets: {}
  });

  const [compareModeActive, setCompareModeActive] = useState(false);
  const [requestsModeActive, setRequestsModeActive] = useState(false);

  const {
    selectedItemList,
    resetSelectedItemList,
    removeItemFromSelectedItemList,
    addItemToSelectedItemList,
    canSelectMore,
    resetListState,
    setSelectedItemList
  } = useSelection(compareModeActive);

  const endCompareRequestMode = () => {
    resetSelectedItemList();
    setCompareModeActive(false);
    setRequestsModeActive(false);
  };

  const [compareBarHeight, setCompareBarHeight] = useState(0);

  const { executeSearch, loading: searchLoading } = useSearch(
    companyAttributesDefinitions,
    productAttributesDefinitions
  );
  const searchContext =
    searchResults?.search?.event != undefined
      ? searchResults.results[0]?.document?.event?.event.name
      : 'Covalo';

  const logSearchRecord = useLogSearchRecord();
  // SIDE EFFECTS ----------------------------
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  useEffect(() => {
    if (isLargeScreen) {
      const HTMLClassList = document.getElementsByTagName('html')[0].classList;
      HTMLClassList.add('search-remove-scroll');
      return () => {
        HTMLClassList.remove('search-remove-scroll');
      };
    }
  }, [isLargeScreen]);

  const handleSearchSuccess = useCallback(
    (response: AxiosResponse<SearchResults>, request: SearchRequest) => {
      const selectedFacetsValues = (
        request?.filters.map(item => item.value) ?? ['']
      ).join(', ');
      // piwik: when a search is complete
      const category = response?.data?.search?.target ?? 'products';
      piwik.push([
        'trackSiteSearch',
        selectedFacetsValues,
        category,
        `${response.data.total}`
      ]);

      setSearchResults(response.data);
    },
    []
  );

  // LOGIC ----------------------------
  const getMoreResults = () => {
    //Were adding 0.5 here, so that at every fourth scroll the popup will appear
    setUserInteractionsCount(prev => prev + 0.5);
    dispatchNewSearchState({ type: 'FETCH_MORE' });
  };

  // ----------------------------
  // HANDLERS
  const handleQueryChange = (query: string) => {
    logSearchRecord(
      query,
      'SUBMITTED_QUERY',
      () => {},
      () => {}
    );
    searchParams.set('query', query);
    setSearchParams(searchParams);
    setUserInteractionsCount(prev => prev + 1);
    dispatchNewSearchState({ type: 'NORMAL', newState: { query } });
  };

  const handleLoadMoreSuccess = useCallback((response: FetchResponse) => {
    setSearchResults(prevResults => ({
      ...response.data,
      results: [...prevResults.results, ...response.data.results]
    }));
  }, []);

  useEffect(() => {
    if (searchState?.filters)
      executeSearch(
        {
          filters: searchState.filters,
          segment: searchState.segment,
          size: searchState.size,
          target: searchState.target,
          eventKey: searchState.eventKey,
          from: searchState.from,
          query: searchState.query,
          sort: searchState.sort
        },
        searchState.source,
        searchState.isFetchingMore
          ? handleLoadMoreSuccess
          : handleSearchSuccess,
        searchState.hideLoadingIndicator
      );
  }, [
    executeSearch,
    searchState,
    handleLoadMoreSuccess,
    handleSearchSuccess,
    isBrandPortal,
    companyAttributesDefinitions,
    productAttributesDefinitions
  ]);

  useEffect(() => {
    if (
      //just at the beginning filters is undefined as an indicator that we have not loaded or initial request yet
      searchState.filters !== undefined ||
      (isBrandPortal &&
        (!companyAttributesDefinitions || !productAttributesDefinitions))
    )
      return;
    dispatchNewSearchState({
      type: 'NORMAL',
      newState: {
        filters: parseUrlFilterParams(
          searchParams,
          target,
          combineCustomAttributes(
            productAttributesDefinitions,
            companyAttributesDefinitions
          )
        )
      }
    });
  }, [
    productAttributesDefinitions,
    companyAttributesDefinitions,
    isBrandPortal,
    target,
    searchParams,
    searchState.filters,
    dispatchNewSearchState
  ]);

  const handleSegmentChange = (segment: SegmentTag) => {
    clearAllUrlFilterParams(searchParams, target);

    searchParams.set('segment', segment.toLowerCase());
    setSearchParams(searchParams);

    setUserInteractionsCount(prev => prev + 1);

    // reset things
    resetSelectedItemList();
    setCompareModeActive(false);
    setRequestsModeActive(false);
    dispatchNewSearchState({
      type: 'NORMAL',
      newState: { segment, filters: [] }
    });
  };

  const handleSortChange = (option: { value: SearchSortField }) => {
    if (searchResults?.search?.target === ProductType.INGREDIENTS) {
      const piwikProp =
        option.value === SearchSortField.SCORE ? 'score' : 'name';
      piwik.push(['trackEvent', 'Search', 'change-sort-order', piwikProp]);
    }
    dispatchNewSearchState({
      type: 'NORMAL',
      newState: {
        sort: [
          {
            field: option.value,
            direction: SortDirection.ASCENDENT
          }
        ]
      }
    });

    setUserInteractionsCount(prev => prev + 1);
  };

  const handleFilterUpdate = (activeFilters: ActiveFilter[]) => {
    //TODO: This logic can be nicer
    const originalFilters = searchState.filters;
    let filters = originalFilters ? [...originalFilters] : [];

    activeFilters.forEach(activeFilter => {
      const filterList: SearchFilter[] = activeFilter.value.map(
        (option: Option) => ({
          field: activeFilter?.property.toLocaleLowerCase(),
          value: option?.value,
          label: option?.label,
          context: option?.context
        })
      );
      const clickedFilter =
        activeFilters.length > 0 && activeFilters.length < 2;

      updateUrlFilterParams(
        searchParams,
        filterList,
        activeFilter.property,
        clickedFilter,
        searchState.filters
      );
      setSearchParams(searchParams);

      filters = getFiltersWithDifferentType(
        filters ?? [],
        activeFilter.property
      ).concat(filterList);
    });

    if (filters && filters.length > 0) {
      const newestFilter = filters[filters.length - 1];

      const piwikValues = {
        page: 'Search page',
        category: `Filters`,
        action: `${newestFilter.field}`,
        name: `${newestFilter.value}`
      };

      if (Array.isArray(newestFilter.value)) {
        //if an array lets send the last added value
        logSearchRecord(
          newestFilter.value[newestFilter.value.length - 1],
          'ENABLED_FACET',
          () => {},
          () => {}
        );

        trackMatomoEvent({
          context: searchContext,
          isAuthenticated: isAuthenticated,
          where: `${piwikValues.page}/${capitalizeFirstLetter(
            searchState.target
          )}`,
          section: piwikValues.category,
          action: piwikValues.action,
          extraDetails: `/${piwikValues.name}`
        });
      } else {
        logSearchRecord(
          newestFilter.value,
          'ENABLED_FACET',
          () => {},
          () => {}
        );
        trackMatomoEvent({
          context: searchContext,
          isAuthenticated: isAuthenticated,
          where: `${piwikValues.page}/${capitalizeFirstLetter(
            searchState.target
          )}`,
          section: piwikValues.category,
          action: piwikValues.action,
          extraDetails: `/${piwikValues.name}`
        });
      }
    }

    // We need to check equality since this function is sometimes called to clear parts of the filters,
    //    without checking if clearing would change the resulting filters at all.
    const sameFilters = arraysAreEqual(filters, originalFilters);
    if (!sameFilters) {
      dispatchNewSearchState({
        type: 'NORMAL',
        newState: { filters: filters }
      });
    }
  };

  const handleToggleFilter = (
    filterValue: string | boolean,
    filterCategory: string
  ) => {
    const activeFiltersForCurrentOption = filterActiveFiltersByKey(
      filterCategory,
      searchState.filters ?? []
    );

    if (activeFiltersForCurrentOption.some(el => el.value === filterValue)) {
      //we neeed to diselect it
      handleFilterUpdate([
        {
          value: activeFiltersForCurrentOption.filter(
            el => el.value !== filterValue
          ),
          property: filterCategory
        }
      ]);
    } else {
      //we need to select it
      handleFilterUpdate([
        {
          value: [
            ...activeFiltersForCurrentOption,
            { value: filterValue, label: '' }
          ],
          property: filterCategory
        }
      ]);
    }
  };
  const handleFilterClear = () => {
    clearAllUrlFilterParams(
      searchParams,
      target,
      combineCustomAttributes(
        productAttributesDefinitions,
        companyAttributesDefinitions
      )
    );
    dispatchNewSearchState({
      type: 'NORMAL',
      newState: { filters: [] }
    });
    setUserInteractionsCount(prev => prev + 1);
    setSearchParams(searchParams);
  };
  const areFilterSelected =
    searchState.filters && searchState.filters.length !== 0;
  const handleToggleBookmarks = () => {
    //TODO: Set search params if needed for bookmark
    dispatchNewSearchState({
      type: 'NORMAL',
      newState: {
        source:
          searchState.source === SearchSource.ALL
            ? SearchSource.BOOKMARKS
            : SearchSource.ALL
      }
    });
  };

  const handleCompareRequestModeRowClick = (
    newSelectedItemList: SearchDocument[]
  ) => {
    setSelectedItemList(newSelectedItemList);
  };

  const logCompareClick = () => {
    const compareItems = selectedItemList.map(item => {
      const positionOnTable = searchResults.results.findIndex(
        product => product.document.old_key === item.old_key
      );
      item.position = positionOnTable;
      return { document: item };
    });
    const data = formatSearchLog(
      {
        results: compareItems,
        search: { query: searchState.query }
      },
      searchState.filters,
      searchState.segment
    );
    logEvent(data, LogEventType.PRODUCT_COMPARED, eventKey);
  };

  const logItemClick = (document: SearchDocument) => {
    const positionOnTable = searchResults.results.findIndex(
      product => product.document.old_key === document.old_key
    );
    document.position = positionOnTable;
    const data = formatSearchLog(
      {
        results: [{ document: document }],
        search: { query: searchState.query }
      },
      searchState.filters,
      searchState.segment
    );
    const eventType =
      target === ProductType.INGREDIENTS
        ? LogEventType.PRODUCT_DETAIL
        : target === ProductType.FORMULATIONS
        ? LogEventType.FORMULATION_DETAIL
        : LogEventType.SERVICE_DETAIL;
    logEvent(data, eventType, eventKey);
  };

  const handleCompareButtonClick = () => {
    trackMatomoEvent({
      context: searchContext,
      isAuthenticated: isAuthenticated,
      where: `Search page/${capitalizeFirstLetter(searchState.target)}`,
      section: 'Compare',
      action: `Button click ${compareModeActive ? 'close' : 'open'}`
    });
    if (!isAuthenticated) {
      piwik.push(['trackEvent', 'Search', `Click unAuthenticated on: Compare`]);
      return;
    }
    piwik.push(['trackEvent', 'Search', `Click authenticated on: Compare`]);
    setRequestsModeActive(false);

    setCompareModeActive(!compareModeActive);
    resetSelectedItemList();
  };

  const handleRequestButtonClick = () => {
    trackMatomoEvent({
      context: searchContext,
      isAuthenticated: isAuthenticated,
      where: `Search page/${capitalizeFirstLetter(searchState.target)}`,
      section: 'Bulk request',
      action: `Button click ${requestsModeActive ? 'close' : 'open'}`
    });
    if (!isAuthenticated) {
      piwik.push([
        'trackEvent',
        'Search',
        `Click unAuthenticated on:Bulk request`
      ]);
      return;
    }
    piwik.push([
      'trackEvent',
      'Search',
      `Click authenticated on: Bulk request`
    ]);
    setCompareModeActive(false);
    setRequestsModeActive(!requestsModeActive);
    resetSelectedItemList();
  };

  // Selection
  const handleSelectedChange = (
    document: SearchDocument,
    selected: boolean
  ) => {
    if (selected) {
      addItemToSelectedItemList(document);
    } else {
      removeItemFromSelectedItemList(document.id);
    }
  };

  const handleBookmarkChangedSuccess = useCallback(() => {
    if (searchState.source === SearchSource.BOOKMARKS) {
      dispatchNewSearchState({
        type: 'BACKGROUND_REFETCH'
      });
    }
  }, [dispatchNewSearchState, searchState.source]);

  // Bookmarks
  const { onBookmarkChange } = useBookmark(handleBookmarkChangedSuccess);

  if (!isAuthenticated && userInteractionsCount === 2) {
    setTimeout(() => {
      setShowRegisterModal(true);
      setUserInteractionsCount(0);
    }, 200);
  }

  return (
    <ThemeProvider
      theme={theme =>
        isInCosmeticsBranding ? getIncosmeticsTheme(theme) : theme
      }
      cssVariableOverriderStyle={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1
      }}
    >
      <Helmet>
        <title>
          {createPageTitle(
            `Search ${
              IS_UXBEAUTY && target === ProductType.FORMULATIONS
                ? 'Concepts'
                : t('reference.productTypes.' + target)
            }`
          )}
        </title>

        <link
          rel="canonical"
          href={`${
            envs.REACT_APP_COVALO_FRONTOFFICE
          }/${target.toLowerCase()}/search`}
        />

        {!IS_UXBEAUTY && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              url: 'https://www.covalo.com/',
              potentialAction: {
                '@type': 'SearchAction',
                target:
                  'https://www.covalo.com/ingredients/search?query={search_term_string}',
                'query-input': 'required name=search_term_string'
              }
            })}
          </script>
        )}
      </Helmet>
      <SearchProvider
        contextState={{
          request: mapSearchStateToRequest(searchState),
          results: searchResults,
          updateFilters: handleFilterUpdate,
          clearAllFilters: handleFilterClear,
          isPortal: isBrandPortal || IS_UXBEAUTY,
          handleToggleFilter: handleToggleFilter,
          facetOrder: facetOrder,
          setFacetOrder: setFacetOrder,
          onBookmarkChange: onBookmarkChange as onBookmarkChange,
          compare: {
            active: compareModeActive,
            list: selectedItemList,
            limit: compareModeActive ? COMPARE_BULK_LIMIT : REQUEST_BULK_LIMIT
          }
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 4,
            minHeight: 0,
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            marginBottom:
              compareModeActive || requestsModeActive
                ? `${compareBarHeight + 10}px`
                : undefined
          }}
        >
          <>
            {IS_UXBEAUTY && (
              <>
                <Typography
                  sx={{
                    color: 'primary',
                    typography: { xs: 'h4', sm: 'h2' }
                  }}
                >
                  <Trans
                    i18nKey={`search.uxbeauty.title.${getProductTypeSingulars(
                      target
                    ).toLowerCase()}`}
                  />
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{
                    color: ({ palette }) => palette.primary.light,
                    pb: 2
                  }}
                >
                  <Trans
                    i18nKey={`search.uxbeauty.description.${getProductTypeSingulars(
                      target
                    ).toLowerCase()}`}
                  />
                </Typography>
              </>
            )}

            {isInCosmeticsBranding && (
              <div
                style={{
                  backgroundColor: 'white',
                  marginBottom: '.5em'
                }}
              >
                <InCosmeticsHeader iconFill="#fff" />
                {target === ProductType.INGREDIENTS && (
                  <SearchHighlightedCategories
                    activeFilters={searchState.filters ?? []}
                    updateFilters={handleFilterUpdate}
                  />
                )}
              </div>
            )}
          </>

          <SearchHeader
            placeholder="Search for INCI, function, …"
            target={target}
            onQueryChange={handleQueryChange}
            onSegmentChange={handleSegmentChange}
            segment={searchState?.segment}
            bookmarksEnabled={searchState.source === SearchSource.BOOKMARKS}
            onToggleBookmarks={handleToggleBookmarks}
            defaultSearchRequestQuery={searchState.query}
            onCompareButtonClick={handleCompareButtonClick}
            onRequestButtonClick={handleRequestButtonClick}
            compareModeActive={compareModeActive}
            requestsModeActive={requestsModeActive}
            isBrandPortal={isBrandPortal}
            searchResultMode={searchResultMode}
            onSearchResultModeChange={mode => {
              localStorage.setItem(SEARCH_RESULT_MODE_LOCAL_STORAGE_KEY, mode);
              setSearchResultMode(mode);
            }}
            companyAttributeDefinitions={companyAttributesDefinitions}
            productAttributeDefinitions={productAttributesDefinitions}
            searchContext={searchContext}
            isAuthenticated={isAuthenticated}
          />
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              flexDirection: 'column',
              minHeight: { xs: '100%', sm: 0 }
            }}
          >
            {searchResultMode === SearchResultMode.LIST && (
              <div className={classes.content}>
                {!IS_UXBEAUTY && (
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      flexDirection: 'column',
                      width: 270,
                      flexShrink: 0,
                      flexGrow: 0
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <SearchFilterClearButton
                        areFilterSelected={!!areFilterSelected}
                        handleFilterClear={handleFilterClear}
                      />

                      <FacetOrderSelector />
                    </Box>

                    <Filters
                      isBrandPortal={isBrandPortal}
                      companyAttributeDefinitions={companyAttributesDefinitions}
                      productAttributeDefinitions={productAttributesDefinitions}
                    />
                  </Box>
                )}

                {IS_UXBEAUTY ? (
                  searchLoading ? (
                    <SpinnerMui />
                  ) : (
                    <SearchListView
                      data={searchResults}
                      setSearchResults={setSearchResults}
                      handleCompareModeRowClick={
                        handleCompareRequestModeRowClick
                      }
                      logItemClick={logItemClick}
                      onLoadMore={getMoreResults}
                      canSelectMore={canSelectMore}
                      isSelected={id => selectedItemList.some(d => d.id === id)}
                      onSelectedChange={handleSelectedChange}
                      selectionModeEnabled={
                        compareModeActive || requestsModeActive
                      }
                      onBookmarkChange={onBookmarkChange}
                    />
                  )
                ) : !isBrandPortal || isCustomerPortal ? (
                  <CovaloTable
                    target={target}
                    sortOption={searchState.sort}
                    setShowRegisterModal={setShowRegisterModal}
                    handleSortChange={handleSortChange}
                    logItemClick={logItemClick}
                    searchResults={searchResults}
                    resetListState={resetListState}
                    loading={searchLoading}
                    error={false}
                    onLoadMore={getMoreResults}
                    isSelected={id => selectedItemList.some(d => d.id === id)}
                    onSelectedChange={handleSelectedChange}
                    canSelectMore={canSelectMore}
                    selectionModeEnabled={
                      compareModeActive || requestsModeActive
                    }
                  />
                ) : (
                  <BrandPortalTable
                    sortOption={searchState.sort}
                    handleSortChange={handleSortChange}
                    target={target}
                    logItemClick={logItemClick}
                    searchResults={searchResults}
                    setShowRegisterModal={setShowRegisterModal}
                    resetListState={resetListState}
                    loading={
                      searchLoading ||
                      companyAttributesLoading ||
                      productAttributesLoading
                    }
                    error={false}
                    onLoadMore={getMoreResults}
                    isSelected={id => selectedItemList.some(d => d.id === id)}
                    onSelectedChange={handleSelectedChange}
                    canSelectMore={canSelectMore}
                    selectionModeEnabled={
                      compareModeActive || requestsModeActive
                    }
                    customAttributesDefinitions={{
                      data: productAttributesDefinitions,
                      loading: productAttributesLoading,
                      error: !!productAttributesError
                    }}
                  />
                )}
              </div>
            )}

            {IS_UXBEAUTY &&
              searchResultMode === SearchResultMode.GRID &&
              (searchLoading ? (
                <SpinnerMui />
              ) : (
                <SearchCardView
                  searchResults={searchResults}
                  onLoadMore={getMoreResults}
                  isSelected={id => selectedItemList.some(d => d.id === id)}
                  onSelectedChange={handleSelectedChange}
                  selectionModeEnabled={compareModeActive || requestsModeActive}
                  canSelectMore={canSelectMore}
                  onBookmarkChange={onBookmarkChange}
                />
              ))}
          </Box>
          {((target === ProductType.INGREDIENTS && compareModeActive) ||
            requestsModeActive) && (
            <CompareRequestBulkRequestBar
              logCompareClick={logCompareClick}
              compareItemList={selectedItemList}
              onClearAllClick={resetSelectedItemList}
              onRemoveItemClick={removeItemFromSelectedItemList}
              compareModeActive={compareModeActive || requestsModeActive}
              searchRequest={mapSearchStateToRequest(searchState)}
              target={target}
              endCompareRequestMode={endCompareRequestMode}
              event={isBrandPortal || IS_UXBEAUTY}
              searchContext
              type={
                compareModeActive
                  ? 'compare'
                  : requestsModeActive
                  ? 'request'
                  : undefined
              }
              setCompareBarHeight={setCompareBarHeight}
              segment={searchState.segment}
              eventKey={eventKey}
            />
          )}
        </Box>
      </SearchProvider>
      <WhyRegisterModal
        show={showRegisterModal && !IS_UXBEAUTY}
        setModal={setShowRegisterModal}
        segment={searchState?.segment}
      />
    </ThemeProvider>
  );
};

export default Search;

const useStyles = tss.create(({ theme }) => ({
  content: {
    flexGrow: 1,
    minHeight: 0,
    display: IS_UXBEAUTY ? 'block' : 'flex',
    gap: '1em',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  }
}));

function arraysAreEqual(
  arr1: SearchFilter[] | undefined,
  arr2: SearchFilter[] | undefined
) {
  if (arr1 && arr2) {
    if (arr1?.length !== arr2?.length) {
      return false;
    }

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }

    return true;
  } else {
    return false;
  }
}
